/**
 * Reorder array immutably.
 * Returns new array with item moved from fromIndex to toIndex.
 */
export const reorderArray = <T>(array: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex) return array;
  if (fromIndex < 0 || fromIndex >= array.length) return array;
  if (toIndex < 0 || toIndex >= array.length) return array;

  const item = array[fromIndex];
  const withoutItem = array.filter((_, i) => i !== fromIndex);

  return [...withoutItem.slice(0, toIndex), item, ...withoutItem.slice(toIndex)];
};

/**
 * Move item between arrays immutably.
 * Returns [newSourceArray, newTargetArray].
 */
export const moveItemBetweenArrays = <T>(
  source: T[],
  target: T[],
  sourceIndex: number,
  targetIndex?: number
): [T[], T[]] => {
  if (sourceIndex < 0 || sourceIndex >= source.length) {
    return [source, target];
  }

  const item = source[sourceIndex];
  const newSource = source.filter((_, i) => i !== sourceIndex);

  const insertAt = targetIndex ?? target.length;
  const clampedIndex = Math.max(0, Math.min(insertAt, target.length));

  const newTarget = [...target.slice(0, clampedIndex), item, ...target.slice(clampedIndex)];

  return [newSource, newTarget];
};

/**
 * Insert item into array at specific index immutably.
 */
export const insertAtIndex = <T>(array: T[], item: T, index?: number): T[] => {
  const insertAt = index ?? array.length;
  const clampedIndex = Math.max(0, Math.min(insertAt, array.length));

  return [...array.slice(0, clampedIndex), item, ...array.slice(clampedIndex)];
};

/**
 * Remove item from array by index immutably.
 */
export const removeAtIndex = <T>(array: T[], index: number): T[] => {
  if (index < 0 || index >= array.length) return array;
  return array.filter((_, i) => i !== index);
};
